import { useState } from 'react';
import { useTheme } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import config from 'config';
import Logo from 'src/assets/newLogo.svg';
import { useAppDispatch, useAppSelector } from 'src/redux';
import { userSelf } from 'src/redux/actions/auth';
import {
  abandonSubscriptionRestore,
  getMySubscription,
  restoreSubscription,
} from 'src/redux/actions/store';
import {
  formatMandateDate,
  waitForMandateActivation,
} from 'src/helpers/subscription';

/**
 * "No, it was a mistake": set up a new mandate that carries the cancelled one
 * on, and walk the buyer through approving it in Razorpay's sheet.
 *
 * `isRestoring` stays true from the request until the outcome is known, so the
 * notice can step aside while the sheet is up — a dialog's focus trap would
 * otherwise fight the sheet's iframe for the keyboard. `restoredId` is the
 * cancelled mandate the buyer just approved a restore for: its webhook can
 * land after the poll gives up, and until then the backend still asks about
 * it.
 */
export const useSubscriptionRestore = () => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { user } = useAppSelector((state) => state.auth);
  const [isRestoring, setIsRestoring] = useState(false);
  const [restoredId, setRestoredId] = useState<string | null>(null);

  const restore = async (subscriptionId: string) => {
    setIsRestoring(true);
    const res = await dispatch(restoreSubscription(subscriptionId));
    if (!restoreSubscription.fulfilled.match(res)) {
      // The question may no longer stand (the period ran out, or a plan was
      // bought meanwhile), so ask the backend again rather than re-offer it.
      await dispatch(getMySubscription());
      setIsRestoring(false);
      return;
    }
    const mandate = res.payload;
    const resumeDate = formatMandateDate(mandate.resume_at);
    // Razorpay can fire ondismiss after a successful approval; that must not
    // be reported as walking away from it.
    let approved = false;

    const rzp = new (window as any).Razorpay({
      key: config.RAZORPAY_KEY,
      name: 'WBTS Pvt. Ltd.',
      description: 'Abekus Pro autopay',
      image: Logo,
      subscription_id: mandate.subscription_id,
      prefill: {
        name: [user?.first_name, user?.last_name].filter(Boolean).join(' '),
        email: user?.email,
      },
      theme: { color: theme.palette.primary.main },
      // A mandate carries no order signature to verify: the backend hears of
      // the approval from Razorpay's webhook, so poll for it.
      handler: async () => {
        approved = true;
        setRestoredId(subscriptionId);
        const { active } = await waitForMandateActivation(
          mandate.subscription_id,
        );
        await dispatch(getMySubscription());
        dispatch(userSelf());
        if (!active) {
          enqueueSnackbar(
            'Your autopay is being set up. Your plan will update in a moment.',
            { variant: 'info', autoHideDuration: 8000 },
          );
        } else {
          enqueueSnackbar(
            mandate.kind === 'trial'
              ? `Your free trial is back on until ${resumeDate}.`
              : `Autopay is back on. Your next payment is on ${resumeDate}.`,
            { variant: 'success', autoHideDuration: 8000 },
          );
        }
        setIsRestoring(false);
      },
      modal: {
        ondismiss: async () => {
          if (approved) return;
          if (mandate.subscription_row_id) {
            await dispatch(
              abandonSubscriptionRestore(mandate.subscription_row_id),
            );
          }
          setIsRestoring(false);
        },
      },
    });
    rzp.open();
  };

  return { restore, isRestoring, restoredId };
};

export default useSubscriptionRestore;
